import { ShieldSecurity } from '@myua/icons/dist/bulk16';
import { Link2 } from '@myua/icons/dist/linear16';
import { TickCircle } from '@myua/icons/dist/bulk24';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link, generatePath } from 'react-router-dom';
import { WithTooltipPopup } from '../../../../../general/components/WithTooltipPopup/WithTooltipPopup';
import { Thumbnail } from '../../../../../general/components/Thumbnail/Thumbnail';
import {
  CHNode,
  CHNodeHasPlotRecognition,
  CHNodeHasThumbnail,
  CHNodeIsFolder,
  CHNodeIsInTest,
  isNodeDownloadAllowed,
} from '../../../../../general/node';
import { hubRoutes, routerFolder, urlThumbnail } from '../../../../../general/urls';
import { selectableLinkProps } from '../../../../../general/utils/selectableLink';
import { Page } from '../../../../models/page';
import { getNodeId, getPage, getShareID } from '../../../app/selectors';
import { createSearchParamsURL } from '../../../search/model';
import { FileManagerRow } from '../../model';
import { NodeIcon } from '../NodeIcon';
import { PlotRecognitionPopover } from './PlotRecognitionPopover';

const Preview: React.FC<{ node: CHNode }> = ({ node }) => {
  const fallback = <NodeIcon node={node} />;
  const thumb = CHNodeHasThumbnail(node) ? (
    <Thumbnail
      className="ch-file-manager__preview-img"
      src={urlThumbnail(node.id)}
      alt=""
      fallback={fallback}
    />
  ) : null;
  const content = thumb ?? fallback;

  return <span className="ch-file-manager__preview">{content}</span>;
};

const Component: React.FC<{ rowData: FileManagerRow; cellData: any }> = ({ rowData }) => {
  const page = useSelector(getPage);
  const nodeID = useSelector(getNodeId);
  const shareID = useSelector(getShareID);
  let link = '';
  if (CHNodeIsFolder(rowData)) {
    if (page !== Page.TRASH) {
      link = routerFolder(rowData.id, shareID ?? '');
    }
  } else {
    if (page === Page.SEARCH && rowData.searchParamsContext) {
      link = createSearchParamsURL(
        rowData.searchParamsContext.query,
        rowData.searchParamsContext.global,
        rowData.searchParamsContext.tags,
        rowData.searchParamsContext.scope,
        rowData.id,
      );
    } else if (page === Page.TRASH) {
      link = generatePath(hubRoutes.trashFile, { fileID: rowData.id });
    } else {
      link = routerFolder(nodeID ?? '', shareID ?? '', rowData.id);
    }
  }
  const isLinkedFile = !CHNodeIsFolder(rowData) && rowData.is_linked;
  // Файл под модерацией юристов (downloading_allowed === false) — показываем бейдж-щит
  // в списке, аналогично каталогу Asset Lab (MYUA-16456).
  const isUnderModeration = !CHNodeIsFolder(rowData) && !isNodeDownloadAllowed(rowData);
  // Индикатор AI-разметки сюжета — на уровне папки-креатива (MYUA-16423);
  // по клику открывает read-only поповер с данными разметки.
  const hasPlotRecognition = CHNodeHasPlotRecognition(rowData);
  const rootClassName = 'ch-file-manager__cell-content_name';
  const children = (
    <>
      <Preview node={rowData} />
      {CHNodeIsInTest(rowData) && <TickCircle className="ch-file-manager__testIcon" />}
      <span className="ch-file-manager__cell-content_name_string">{rowData.name}</span>
      {hasPlotRecognition && <PlotRecognitionPopover nodeId={rowData.id} />}
      {isLinkedFile && (
        <WithTooltipPopup
          text="This file is linked to creative"
          trigger={
            <span
              className="ch-file-manager__linked-indicator"
              aria-label="This file is linked to creative"
            >
              <Link2 />
            </span>
          }
        />
      )}
      {isUnderModeration && (
        <WithTooltipPopup
          text="File is under legal moderation"
          trigger={
            <span
              className="ch-file-manager__moderation-indicator"
              aria-label="File is under legal moderation"
            >
              <ShieldSecurity />
            </span>
          }
        />
      )}
    </>
  );

  return link ? (
    <Link to={link} className={rootClassName} title={rowData.name} {...selectableLinkProps}>
      {children}
    </Link>
  ) : (
    <span className={rootClassName} title={rowData.name}>
      {children}
    </span>
  );
};

export const NameCell = (props: any) => <Component {...props} />;
